import { datadogLogs } from "@datadog/browser-logs";
import { RateSource } from "@redotech/money/rate-source";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  useTriggerLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import {
  FreightBookingModal,
  fulfillmentGroupToFreightProducts,
} from "@redotech/redo-merchant-app-common/freight/freight-booking-modal";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import type { PurchaseFreightBookingResponse } from "@redotech/redo-model/freight/freight-booking";
import {
  FulfillmentGroupData,
  FulfillmentGroupPickStatus,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import {
  canVoidFreight,
  canVoidLabel,
} from "@redotech/redo-model/fulfillments/fulfillment-group-utils";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import { PrintableDocument } from "@redotech/redo-model/fulfillments/print-job";
import { FulfillmentGroupActions } from "@redotech/redo-model/outbound-labels/fulfillment-group-actions";
import { Parcel } from "@redotech/redo-model/outbound-labels/parcel";
import { Preset } from "@redotech/redo-model/outbound-labels/presets";
import { toast } from "@redotech/redo-web/alert";
import {
  RedoButton,
  RedoButtonSize,
} from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActionButtonMessage } from "../../common/action-button-message";
import { AddFundsModalContext } from "../../common/add-funds";
import {
  BinSelectionModal,
  InventoryAdjustmentWarningModal,
  useBinSelectionModal,
} from "../../common/bin-selection-modal";
import { CancelOrderModalContext } from "../../common/cancel-order/cancel-order-context";
import { CreateReturnLabelModalContext } from "../../common/create-return-label/create-return-label-context";
import { EditManualOrderSlideout } from "../../common/edit-manual-order-slideout";
import { doOMSAction } from "../../common/fulfillment-group-actions";
import { FulfillmentGroupActionsButton } from "../../common/fulfillment-group-actions-button";
import { MarkAsFulfilledModalContext } from "../../common/mark-as-fulfilled/mark-as-fulfilled-context";
import { MissingItemsModalContext } from "../../common/missing-items/missing-items-modal-context";
import { PackingSlipBinModalContext } from "../../common/packing-slip-bin-modal-provider";
import {
  PinVerificationType,
  usePinVerification,
} from "../../common/pin-verification-context";
import { PlaceOnHoldModalContext } from "../../common/place-on-hold/place-on-hold-context";
import {
  getDocumentPrintStatusMapForFulfillmentGroups,
  PrintBatchContext,
} from "../../common/print-batch-modal";
import { ReshipModal } from "../../common/reship-modal";
import { ShipmentFlagsBadge } from "../../common/shipment-flags-badge";
import { SubmitShippingClaimModalContext } from "../../common/shipping-claim";
import { UnmergeModal } from "../../common/unmerge-modal";
import { actionMessages, calculateNeedsFunds } from "../../common/util";
import { useDeviceLocation } from "../../hooks/use-device-location";
import { useShipmentFlags } from "../../hooks/use-shipment-flags";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { getTeamOMSUsers } from "../../utils/get-team-oms-users";
import { canRequestGetNewRates } from "../../utils/outbound-labels-utils";
import { useFulfillmentGroupPermissions } from "../../utils/use-assigned-fulfillment-service";
import { useGroupInfo } from "../../utils/use-group-info";
import { OverrideNotReceivedModal } from "../override-not-received-modal";

export const ActionButtons = memo(function ActionButtons({
  buttonLabels,
  buttonSize,
  displayedButtonAction,
  fulfillmentGroups,
  fulfillmentDate,
  notifyMarketplace,
  selectedStatus,
  selectedPreset,
  setRefreshSymbol,
  updatesPending,
  mixedPackingSlipSizes,
  canPurchaseEveryGroup,
  parcelValues,
  hideSecondaryActions,
  hideRate,
  onGroupsFulfillment,
  hideMessages,
  mixedPersonalizedSlipSizes,
  purchasableGroups,
  deselectNonPurchasableGroups,
  dropdownClassName,
}: {
  buttonLabels?: Partial<Record<FulfillmentGroupActions, string>>;
  buttonSize?: RedoButtonSize;
  displayedButtonAction: FulfillmentGroupActions;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  fulfillmentDate: Date | null;
  notifyMarketplace: boolean;
  selectedPreset: Preset | undefined;
  selectedStatus: FulfillmentGroupStatus | undefined | null;
  setRefreshSymbol: (symbol: symbol) => void;
  updatesPending: boolean;
  mixedPackingSlipSizes: boolean;
  mixedPersonalizedSlipSizes: boolean;
  canPurchaseEveryGroup: boolean;
  parcelValues: readonly Parcel[];
  hideSecondaryActions?: boolean;
  hideRate?: boolean;
  onGroupsFulfillment: (groupIds: string[]) => void;
  hideMessages?: boolean;
  purchasableGroups: readonly FulfillmentGroupData[];
  deselectNonPurchasableGroups?: (groupIds: string[]) => void;
  dropdownClassName?: string;
}) {
  const [deviceLocationId] = useDeviceLocation();
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const inventoryRpcClient = useContext(RedoInventoryRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const user = useContext(UserContext);
  const openMarkAsFulfilledModal = useContext(MarkAsFulfilledModalContext);
  const openCancelOrderModal = useContext(CancelOrderModalContext);
  const missingItemsModal = useContext(MissingItemsModalContext);
  const navigate = useNavigate();
  const { executeWithPinCheck } = usePinVerification();

  const teamUsers = getTeamOMSUsers(team);

  const { permissionsToEditEveryGroup } =
    useFulfillmentGroupPermissions(fulfillmentGroups);

  const {
    selectedRate,
    deliveryMethod,
    selectedParcelsPerGroup,
    mixedParcels,
    totalWeight,
    ratesPending,
    totalCustomsDeclarationWeight,
    destinationAddress,
    originAddress,
    totalRatePrice,
    totalChargedToRedoBalance,
  } = useGroupInfo({
    fulfillmentGroups,
    parcelValues,
    availableUsers: teamUsers,
    permissionsToEditEveryGroup,
    team,
  });

  const flags = useShipmentFlags(
    fulfillmentGroups,
    selectedRate,
    team,
    fulfillmentDate,
  );

  const [balanceLoad, reloadBalance] = useTriggerLoad(
    async (signal: AbortSignal) => {
      try {
        return await client.getOutboundBalance(null, { signal });
      } catch (e) {
        return { balance: "0" };
      }
    },
  );

  useEffect(() => {
    reloadBalance();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const balance = balanceLoad?.value;

  const needsFunds = useMemo(() => {
    return calculateNeedsFunds(
      balance?.balance,
      totalChargedToRedoBalance,
      canPurchaseEveryGroup,
      team?.settings?.outboundLabels?.billing?.autoReload?.floor ?? 0,
    );
  }, [balance, team, totalChargedToRedoBalance, canPurchaseEveryGroup]);

  const currentAction = useRef<FulfillmentGroupActions | null>(null);
  const performAction = useHandler((action: FulfillmentGroupActions) => {
    // Log potential double-calls when action is already in progress
    if (actionLoad.pending) {
      console.error(
        "Detected double-call to performAction while action is pending (outbound-order)",
        { action, currentAction: currentAction.current },
      );
    }

    currentAction.current = action;

    if (
      (action === FulfillmentGroupActions.CREATE_LABEL ||
        action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL) &&
      totalRatePrice !== null &&
      totalRatePrice !== undefined
    ) {
      executeWithPinCheck(
        () => void doAction(),
        { type: PinVerificationType.ExpensiveLabel, labelCost: totalRatePrice },
        { title: "Expensive Label PIN Verification" },
      );
    } else {
      void doAction();
    }
  });

  const batchPrinting = useContext(PrintBatchContext);
  const openPackingSlipBinModal = useContext(PackingSlipBinModalContext);

  const [editManualOrderModalOpen, setEditManualOrderModalOpen] =
    useState(false);
  const placeOnHoldContext = useContext(PlaceOnHoldModalContext);
  const placeOnHold = useHandler(() => {
    placeOnHoldContext(fulfillmentGroups.map(({ _id }) => _id));
  });

  const addFundsContext = useContext(AddFundsModalContext);
  const addFunds = useHandler(() => {
    addFundsContext({
      labelPrice: totalRatePrice ?? 0,
      balance: balance?.balance,
      balanceLoading: balanceLoad.pending,
      onSuccess: () => {
        performAction(FulfillmentGroupActions.CREATE_AND_PRINT_LABEL);
      },
    });
  });

  const editManualOrder = useHandler(() => {
    setEditManualOrderModalOpen(true);
  });

  const submitShippingClaim = useContext(SubmitShippingClaimModalContext);
  const submitCarrierClaim = useHandler(() => {
    if (!fulfillmentGroups?.[0]) {
      return;
    }
    submitShippingClaim(fulfillmentGroups?.[0]);
  });

  const viewSubmittedClaim = useHandler((claimId: string) => {
    navigate(
      `${routes.getClaimsSubRoute("carrier-claims")}?selected=${claimId}`,
    );
  });

  const startReturn = useHandler((orderId: string) => {
    navigate(
      `/stores/${team?._id}/order-management/returns/create-return/${orderId}`,
    );
  });

  const createReturnLabel = useContext(CreateReturnLabelModalContext);

  const openUnmergeModal = useHandler(() => {
    setUnmergeModalOpen(true);
  });

  const handleUnmerge = useHandler(async (fulfillmentOrderIds: string[]) => {
    try {
      if (fulfillmentGroups.length !== 1) {
        return;
      }

      const fulfillmentGroup = fulfillmentGroups[0];
      const totalFulfillmentOrders =
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fulfillmentGroup.orders?.filter((o) => o.fulfillmentOrderExternalId)
          .length ?? 0;

      if (fulfillmentOrderIds.length === totalFulfillmentOrders) {
        await client.unmergeFulfillmentGroups({
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          fulfillmentGroupIds: [fulfillmentGroup._id],
        });
      } else {
        await client.unmergeFulfillmentOrdersFromGroups({
          unmergeRequests: [
            {
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              fulfillmentGroupId: fulfillmentGroup._id,
              fulfillmentOrderIdsToUnmerge: fulfillmentOrderIds,
            },
          ],
        });
      }

      toast("Successfully unmerged shipments", { variant: "success" });
      setRefreshSymbol(Symbol());
    } catch (error) {
      toast("Error unmerging shipments", { variant: "error" });
    }
  });

  const openMissingItemsModal = useHandler(() => {
    if (fulfillmentGroups.length === 1) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      missingItemsModal.openModal(fulfillmentGroups[0], {
        onFulfillmentGroupUpdated: () => {
          setRefreshSymbol(Symbol());
        },
      });
    }
  });

  const navigateToProblemOrderDetails = useHandler(() => {
    if (fulfillmentGroups.length === 1) {
      navigate(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        `/stores/${team?._id}/order-management/outbound-labels/pick-sessions/problem-order/${fulfillmentGroups[0]._id}`,
      );
    }
  });

  const [cancelFreightLoad, triggerCancelFreight] = useTriggerLoad(
    async (signal) => {
      try {
        const groupId = fulfillmentGroups[0]?._id;
        if (!groupId) {
          return;
        }
        await client.cancelFreightBooking(
          { fulfillmentGroupId: groupId },
          { signal },
        );
        toast("Freight booking cancelled", { variant: "success" });
        setRefreshSymbol(Symbol());
      } catch (error) {
        toast("Failed to cancel freight booking", { variant: "error" });
      }
    },
  );

  const showCancelFreightButton =
    fulfillmentGroups.length === 1 &&
    fulfillmentGroups[0] !== undefined &&
    canVoidFreight(fulfillmentGroups[0]);

  const createPurchaseOrder = useHandler(async (fulfillmentGroupId: string) => {
    if (!inventoryRpcClient) {
      toast("Inventory module is not available", {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
      return;
    }
    try {
      const result = await inventoryRpcClient.createPurchaseOrder({
        fulfillmentGroupId,
      });
      navigate(
        `${routes.inventoryManagementBaseUrl}/purchase-orders/all/${result.purchaseOrderId}`,
      );
    } catch (error) {
      console.error("Failed to create purchase order:", error);
      toast("Failed to create purchase order", {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
    }
  });

  const viewPurchaseOrder = useHandler((purchaseOrderId: string) => {
    navigate(
      `${routes.inventoryManagementBaseUrl}/purchase-orders/all/${purchaseOrderId}`,
    );
  });

  const isProblemOrder =
    fulfillmentGroups.length === 1 &&
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    fulfillmentGroups[0].pickStatus === FulfillmentGroupPickStatus.PROBLEM;

  const [actionLoad, doAction] = useTriggerLoad(
    async (signal?: AbortSignal) => {
      await doOMSAction(
        {
          fulfillmentDate,
          action: currentAction.current,
          user,
          client,
          fulfillmentGroups,
          purchasableGroups,
          team,
          notifyMarketplace,
          resetSelections: () => {},
          reloadBalance,
          placeOnHold,
          triggerRefresh: () => {
            setRefreshSymbol(Symbol());
          },
          printBatch: batchPrinting.printBatch,
          addFunds,
          editManualOrder,
          mixedPackingSlipSizes,
          mixedPersonalizedSlipSizes,
          openMarkAsFulfilledModal,
          submitCarrierClaim,
          startReturn,
          onGroupsFulfillment,
          openPackingSlipBinModal,
          openCancelOrderModal,
          createReturnLabel,
          openOverrideNotReceivedModal: () =>
            setOverrideNotReceivedModalOpen(true),
          openUnmergeModal,
          openMissingItemsModal,
          openFreightBookingModal: () => setFreightBookingModalOpen(true),
          createPurchaseOrder,
          viewPurchaseOrder,
          navigateToScanAndVerify: (fulfillmentGroupId: string) => {
            navigate(
              `/stores/${team._id}/order-management/outbound-labels/scan/${fulfillmentGroupId}`,
            );
          },
          deselectNonPurchasableGroups,
          openBinSelectionModal,
          openInventoryAdjustmentWarningModal,
          executeWithPinCheck,
          deviceLocationId,
        },
        signal,
      );
    },
  );

  const {
    errorMessage: groupErrorMessage,
    warningMessage: groupWarningMessage,
  } = actionMessages({
    currentValue: displayedButtonAction,
    fulfillmentGroups,
    selectedParcels: selectedParcelsPerGroup[0]!,
    mixedParcels,
    totalRatePrice,
    needsFunds,
    balance: balance?.balance,
    balanceLoading: balanceLoad.pending,
    selectedPreset,
    selectedRate,
    deliveryMethod,
    ratesPending,
    totalCustomsDeclarationWeight,
    totalWeight,
    destinationAddress,
    originAddress,
  });

  const errorMessage: string =
    groupErrorMessage +
    (fulfillmentGroups
      .map((group) => group.providerActionMessages?.errorMessage)
      .filter(Boolean)
      .join(" ") ?? "");

  const warningMessage: string =
    groupWarningMessage +
    (fulfillmentGroups
      .map((group) => group.providerActionMessages?.warningMessage)
      .filter(Boolean)
      .join(" ") ?? "");

  const anyGroupPending = useMemo(() => {
    return fulfillmentGroups.some(
      (g) => g.status === FulfillmentGroupStatus.Pending,
    );
  }, [fulfillmentGroups]);

  const [voidShipmentLoad, triggerVoidShipmentLoad] = useTriggerLoad(
    async (signal) => {
      try {
        const res = await client.voidShipments(
          { fulfillmentGroupIds: fulfillmentGroups.map(({ _id }) => _id) },
          { signal },
        );
        if (res.success) {
          toast("Successfully voided label", { variant: "success" });
        }
      } catch (err) {
        toast("Failed to void label", { variant: "error" });
      } finally {
        setRefreshSymbol(Symbol());
      }

      return;
    },
  );

  const [_, triggerRefreshRates] = useTriggerLoad(async (signal) => {
    try {
      toast("Getting updated rates...", { variant: "success" });
      await client.refreshShippingRates(
        { fulfillmentGroupIds: fulfillmentGroups.map(({ _id }) => _id) },
        { signal },
      );
    } catch (err) {
      toast("Failed to refresh rates", { variant: "error" });
    }
  });

  const showReshipButton = fulfillmentGroups.every(
    (group) => group.status === FulfillmentGroupStatus.Closed,
  );

  const [duplicateShipmentLoad, triggerCreateDuplicateShipment] =
    useTriggerLoadWithInput(
      async (lineItemExternalIds: string[] | undefined, signal) => {
        try {
          await client.duplicateShipments(
            {
              fulfillmentGroupIds: fulfillmentGroups.map(({ _id }) => _id),
              lineItemExternalIdsToInclude: lineItemExternalIds,
            },
            { signal },
          );
          toast("Successfully reshipped", { variant: "success" });
        } catch (err) {
          toast("Failed to reship", { variant: "error" });
        } finally {
          setRefreshSymbol(Symbol());
        }
      },
    );

  const [reshipModalOpen, setReshipModalOpen] = useState(false);
  const [overrideNotReceivedModalOpen, setOverrideNotReceivedModalOpen] =
    useState(false);
  const [unmergeModalOpen, setUnmergeModalOpen] = useState(false);
  const [freightBookingModalOpen, setFreightBookingModalOpen] = useState(false);
  const {
    modalProps: binSelectionModalProps,
    inventoryAdjustmentWarningModalProps,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
  } = useBinSelectionModal();

  const handleFreightPurchase = useHandler(
    async (response: PurchaseFreightBookingResponse) => {
      const groupId = fulfillmentGroups[0]?._id;
      if (!groupId) {
        return;
      }
      await client.fulfillFulfillmentGroupWithFreightBooking({
        fulfillmentGroupId: groupId,
        freightBookingId: response.freightBookingId,
      });
      batchPrinting.printBatch({
        document: PrintableDocument.BILL_OF_LADING,
        batch: { fulfillmentGroupIds: [groupId] },
        documentPrintStatusMap:
          getDocumentPrintStatusMapForFulfillmentGroups(fulfillmentGroups),
      });
    },
  );

  const canGetNewRates = useMemo(() => {
    return fulfillmentGroups.some((group) => canRequestGetNewRates(group));
  }, [fulfillmentGroups]);

  if (!fulfillmentGroups?.length) {
    return null;
  }

  const isOnIms =
    team.settings.ims?.enabled &&
    team.settings.ims.status === "source-of-truth";

  return (
    <Flex dir="column" gap="md">
      <BinSelectionModal {...binSelectionModalProps} />
      <InventoryAdjustmentWarningModal
        {...inventoryAdjustmentWarningModalProps}
      />
      <OverrideNotReceivedModal
        fulfillmentGroups={fulfillmentGroups}
        isOpen={overrideNotReceivedModalOpen}
        onClose={() => setOverrideNotReceivedModalOpen(false)}
      />
      <UnmergeModal
        fulfillmentGroup={fulfillmentGroups[0] ?? null}
        isOpen={unmergeModalOpen}
        onClose={() => setUnmergeModalOpen(false)}
        onUnmerge={handleUnmerge}
      />
      <ReshipModal
        isOnIms={!!isOnIms}
        isOpen={reshipModalOpen}
        items={
          fulfillmentGroups.length === 1 ? fulfillmentGroups[0]!.items : []
        }
        onCancel={() => setReshipModalOpen(false)}
        onSuccess={(selectedLineItemExternalIds) => {
          setReshipModalOpen(false);
          triggerCreateDuplicateShipment(selectedLineItemExternalIds);
        }}
      />
      {fulfillmentGroups.length === 1 &&
        fulfillmentGroups[0] &&
        originAddress &&
        destinationAddress && (
          <FreightBookingModal
            balance={balance?.balance ?? "0"}
            customerReference1={fulfillmentGroups[0].orders[0]?.name}
            destinationAddress={
              (fulfillmentGroups[0]
                .verifiedDestinationAddress as FulfillmentOrderAddress) ??
              destinationAddress
            }
            freightProducts={fulfillmentGroupToFreightProducts(
              fulfillmentGroups[0],
            )}
            isOpen={freightBookingModalOpen}
            key={`freight-booking-${fulfillmentGroups[0]._id}`}
            onClose={() => setFreightBookingModalOpen(false)}
            onPurchase={handleFreightPurchase}
            originAddress={
              (fulfillmentGroups[0]
                .verifiedOriginAddress as FulfillmentOrderAddress) ??
              originAddress
            }
          />
        )}
      {fulfillmentGroups?.[0] &&
        fulfillmentGroups.length === 1 &&
        !hideSecondaryActions && (
          <EditManualOrderSlideout
            fulfillmentGroupData={fulfillmentGroups[0]}
            key={`edit-manual-order-${fulfillmentGroups[0]._id}`}
            onClose={() => setEditManualOrderModalOpen(false)}
            open={editManualOrderModalOpen}
            triggerRefresh={() => setRefreshSymbol(Symbol())}
          />
        )}
      <Flex align="flex-end" justify="flex-end">
        <Flex align="flex-end" gap="3xl" justify="flex-end">
          {totalRatePrice != undefined &&
            !hideSecondaryActions &&
            !hideRate && (
              <Flex
                align="flex-end"
                flexDirection="column"
                gap="xs"
                justify="flex-end"
              >
                <Text fontSize="xs" textColor="secondary">
                  Rate
                </Text>
                <Text fontSize="xs" fontWeight="bold">
                  <MoneyText
                    source={RateSource.Stripe}
                    value={toMoney({ amount: totalRatePrice })}
                  />
                </Text>
                {canGetNewRates && (
                  <Flex align="center" gap="none">
                    <Text
                      cursor="pointer"
                      fontSize="xs"
                      fontWeight="thin"
                      hoverTextColor="primary"
                      onClick={() => {
                        triggerRefreshRates();
                      }}
                      pt="none"
                      textColor="tertiary"
                      textDecoration="underline"
                    >
                      Get updated rates
                    </Text>
                  </Flex>
                )}
              </Flex>
            )}
          <Flex gap="md">
            {isProblemOrder && !hideSecondaryActions && (
              <RedoButton
                hierarchy="secondary"
                onClick={navigateToProblemOrderDetails}
                size={buttonSize ?? "md"}
                text="View problem order details"
                theme="normal"
              />
            )}
            {showReshipButton && !hideSecondaryActions && (
              <RedoButton
                disabled={!permissionsToEditEveryGroup}
                hierarchy={buttonSize ? "secondary" : "primary"}
                onClick={() =>
                  executeWithPinCheck(
                    () => setReshipModalOpen(true),
                    { type: PinVerificationType.Reship },
                    {
                      title: "Reship PIN Verification",
                      description:
                        "Please enter your PIN to proceed with reshipping these items.",
                    },
                  )
                }
                pending={duplicateShipmentLoad.pending}
                size={buttonSize ?? "md"}
                text="Reship"
                theme="normal"
              />
            )}
            {fulfillmentGroups.every(canVoidLabel) && !hideSecondaryActions && (
              <RedoButton
                disabled={!permissionsToEditEveryGroup}
                hierarchy="secondary"
                onClick={() => {
                  const voidLabelText =
                    fulfillmentGroups.length > 1
                      ? `Void ${fulfillmentGroups.length} labels`
                      : "Void label";
                  executeWithPinCheck(
                    () => {
                      datadogLogs.logger.info("Void label confirmed", {
                        fulfillmentGroupIds: fulfillmentGroups.map(
                          ({ _id }) => _id,
                        ),
                        teamId: team?._id,
                        userId: user?._id,
                        source: "fulfillment-app",
                        action: "Void label",
                      });
                      triggerVoidShipmentLoad();
                    },
                    { type: PinVerificationType.VoidLabel },
                    {
                      title: "Void Label PIN Verification",
                      description:
                        "Please enter your PIN to proceed with voiding the label.",
                    },
                    {
                      title: voidLabelText,
                      description:
                        fulfillmentGroups.length > 1
                          ? "Please confirm that you want to void these labels"
                          : "Please confirm that you want to void this label",
                      confirmText: voidLabelText,
                      theme: "error",
                    },
                  );
                }}
                pending={voidShipmentLoad.pending}
                size={buttonSize ?? "md"}
                text="Void label"
                theme="destructive"
              />
            )}
            {showCancelFreightButton && !hideSecondaryActions && (
              <RedoButton
                disabled={!permissionsToEditEveryGroup}
                hierarchy="secondary"
                onClick={() => triggerCancelFreight()}
                pending={cancelFreightLoad.pending}
                size={buttonSize ?? "md"}
                text="Cancel freight"
                theme="destructive"
              />
            )}

            <FulfillmentGroupActionsButton
              buttonLabels={buttonLabels}
              buttonSize={buttonSize}
              canPurchaseEveryGroup={canPurchaseEveryGroup}
              displayedButtonAction={displayedButtonAction}
              dropdownClassName={dropdownClassName}
              errorMessage={errorMessage}
              everythingDisabled={!permissionsToEditEveryGroup}
              fulfillmentGroups={fulfillmentGroups}
              insufficientFunds={needsFunds}
              loading={actionLoad.pending || updatesPending}
              onClick={performAction}
              purchasableGroups={purchasableGroups}
              selectedStatus={selectedStatus}
              viewSubmittedClaim={viewSubmittedClaim}
              warningMessage={warningMessage}
            />
          </Flex>
        </Flex>
      </Flex>
      {flags.length > 0 && (
        <Flex justify="flex-end">
          <ShipmentFlagsBadge flags={flags} />
        </Flex>
      )}
      {!hideMessages && (
        <ActionButtonMessage
          anyGroupPending={anyGroupPending}
          errorMessage={errorMessage}
          fulfillmentGroups={fulfillmentGroups}
          insufficientFunds={needsFunds}
          warningMessage={warningMessage}
        />
      )}
    </Flex>
  );
});
